import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, Mail, Phone, MapPin, Clock, Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, formatINR } from "@/lib/api";

export default function Confirmation() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/orders/${orderId}`);
        setOrder(res.data);
      } catch (e) {
        setOrder(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [orderId]);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm">
        Loading your order…
      </main>
    );
  }
  if (!order) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center text-slate-500 text-sm">
        Order not found.
      </main>
    );
  }

  const shortId = order.id.slice(0, 8).toUpperCase();

  return (
    <main className="min-h-[calc(100vh-5rem)] bg-slate-50">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div
          data-testid="lcost-order-success-card"
          className="bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-sm"
        >
          <div className="bg-gradient-to-br from-[#1a56db] to-[#0e9f6e] px-6 sm:px-8 py-7 sm:py-9 text-white relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/20 blur-2xl" />
            <div className="relative">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-white text-[#0e9f6e] mb-4">
              <CheckCircle2 className="w-8 h-8" strokeWidth={2.5} />
            </div>
            <div className="text-xs font-semibold uppercase tracking-wider opacity-90">
              Pickup confirmed
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">
              You’re getting <span className="bg-white text-[#057a55] px-2 rounded-lg">{formatINR(order.quote_amount)}</span>
            </h1>
            <div className="mt-3 inline-flex items-center gap-2 bg-white/15 px-3 py-1.5 rounded-full text-xs font-mono tracking-wider">
              Order #{shortId}
            </div>
            </div>
          </div>

          <div className="p-6 sm:p-8">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">
              Pickup details
            </div>
            <div className="space-y-4">
              <Row icon={<CalendarIcon className="w-4 h-4" />} label="Pickup on">
                {order.pickup_date} · <span className="font-mono">{order.pickup_slot}</span>
              </Row>
              <Row icon={<MapPin className="w-4 h-4" />} label="Address">
                {order.customer.address}, {order.customer.city}
              </Row>
              <Row icon={<Phone className="w-4 h-4" />} label="Contact">
                {order.customer.name} · {order.customer.phone}
              </Row>
              <Row icon={<Mail className="w-4 h-4" />} label="Confirmation sent to">
                {order.customer.email}
              </Row>
            </div>

            <div className="mt-6 p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Device
              </div>
              <div className="font-display font-bold text-slate-900">
                {order.brand_name} {order.model_name}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Keep the device, original charger (if available) and a photo ID ready at pickup.
              </div>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/" className="flex-1">
                <Button
                  variant="outline"
                  className="w-full h-11 border-slate-300"
                >
                  Back to home
                </Button>
              </Link>
              <Link to="/sell" className="flex-1">
                <Button className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white">
                  Sell another phone
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-7 h-7 rounded-lg bg-blue-50 text-[#1e429f] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div className="text-sm text-slate-900 font-medium">{children}</div>
      </div>
    </div>
  );
}
